import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { moderationAPI, hubError } from '../lib/api';
import { useLiveEvents } from '../context/LiveEventsContext';
import { TopBar } from '../components/layout/TopBar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Flag, Loader2, CheckCircle, XCircle, User } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination } from '../components/Pagination';
import { usePagination } from '../hooks/usePagination';

const statusMap = {
  pending: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-700' },
  action_taken: { label: 'Atendido', color: 'bg-green-100 text-green-700' },
  dismissed: { label: 'Descartado', color: 'bg-gray-100 text-gray-700' },
};

const contentLabels = { petition: 'Petición', post: 'Noticia', comment: 'Comentario', event: 'Evento', prayer: 'Oración' };
const LIVE_REPORT_EVENTS = ['report.created'];

export default function ModerationPage() {
  const { canUpdate } = useAuth();
  const { setMobileMenuOpen, churchInfo } = useOutletContext();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('pending');
  const [resolving, setResolving] = useState(null);
  const [action, setAction] = useState('action_taken');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [total, setTotal] = useState(0);
  const pg = usePagination(status);

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.all([
      moderationAPI.reports(status === 'all' ? undefined : status, pg.params),
      moderationAPI.counts(),
    ])
      .then(([reportsRes, countsRes]) => {
        setItems(reportsRes.data?.data || []);
        setTotal(reportsRes.data?.total || 0);
        setCounts(countsRes.data?.data || null);
      })
      .catch(err => {
        if (err?.response?.status === 404) setUnavailable(true);
        else toast.error(hubError(err, 'Error al cargar reportes'));
      })
      .finally(() => setLoading(false));
  }, [status, pg.params]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useLiveEvents(LIVE_REPORT_EVENTS, (events) => {
    toast.warning(`${events.length} reporte${events.length === 1 ? '' : 's'} nuevo${events.length === 1 ? '' : 's'} de la congregación`);
    fetchData();
  });

  const openResolve = (report, defaultAction) => {
    setResolving(report);
    setAction(defaultAction);
    setNote('');
  };

  const handleResolve = async () => {
    setSaving(true);
    try {
      await moderationAPI.resolve(resolving.id, action, note || undefined);
      toast.success(action === 'action_taken' ? 'Reporte atendido' : 'Reporte descartado');
      setResolving(null);
      fetchData();
    } catch (err) { toast.error(hubError(err, 'Error al resolver el reporte')); } finally { setSaving(false); }
  };

  return (
    <div>
      <TopBar title="Moderación" onMenuClick={() => setMobileMenuOpen(true)} churchInfo={churchInfo} />
      {unavailable ? (
        <div className="px-4 sm:px-6 lg:px-8 py-5">
          <div className="empty-state" data-testid="moderation-unavailable">
            <Flag className="w-12 h-12 text-muted-foreground/40" />
            <p className="font-medium">Moderación aún no disponible</p>
            <p className="text-sm text-muted-foreground max-w-md">
              El servidor de Tu Iglesia App todavía no expone los reportes de contenido en este entorno.
              Cuando se publiquen, verás aquí las denuncias de la congregación en tiempo real.
            </p>
          </div>
        </div>
      ) : (
      <div className="px-4 sm:px-6 lg:px-8 py-5">
        <div className="grid grid-cols-3 gap-4 mb-6">
          {['pending', 'action_taken', 'dismissed'].map(key => (
            <Card key={key} className="stat-card">
              <CardContent className="p-4">
                <p className="text-2xl font-semibold" style={{ color: key === 'pending' ? '#C58B2A' : '#056A84' }} data-testid={`moderation-count-${key}`}>
                  {counts?.[key] ?? 0}
                </p>
                <p className="text-xs text-muted-foreground">{statusMap[key].label}s</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs value={status} onValueChange={setStatus} className="mb-4">
          <TabsList>
            <TabsTrigger value="pending" data-testid="moderation-filter-pending">Pendientes</TabsTrigger>
            <TabsTrigger value="action_taken" data-testid="moderation-filter-action">Atendidos</TabsTrigger>
            <TabsTrigger value="dismissed" data-testid="moderation-filter-dismissed">Descartados</TabsTrigger>
            <TabsTrigger value="all" data-testid="moderation-filter-all">Todos</TabsTrigger>
          </TabsList>
        </Tabs>

        <Pagination
          total={total} page={pg.page} pageSize={pg.pageSize}
          onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize}
          label="reportes" testId="moderation-pagination"
        />

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 spinner text-[#056A84]" /></div>
        ) : items.length === 0 ? (
          <div className="empty-state"><Flag className="w-12 h-12 text-muted-foreground/40" /><p>No hay reportes en esta categoría</p></div>
        ) : (
          <div className="space-y-3">
            {items.map(item => {
              const si = statusMap[item.status] || statusMap.pending;
              return (
                <Card key={item.id} className="stat-card" data-testid={`report-card-${item.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-xl bg-[#D64545]/10 flex items-center justify-center shrink-0">
                          <Flag className="w-5 h-5 text-[#D64545]" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-[10px]">{contentLabels[item.content_type] || item.content_type}</Badge>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${si.color}`}>{si.label}</span>
                            <span className="text-xs text-muted-foreground">Motivo: {item.reason}</span>
                          </div>
                          {item.content_snippet && <p className="text-sm mt-1.5 line-clamp-2">"{item.content_snippet}"</p>}
                          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                            <User className="w-3 h-3" /> Reportado por {item.reporter_name || 'miembro'} · {item.created_at ? new Date(item.created_at).toLocaleDateString('es-ES') : ''}
                          </p>
                          {item.resolution_note && <p className="text-xs text-muted-foreground mt-1">Nota: {item.resolution_note}</p>}
                        </div>
                      </div>
                      {canUpdate('moderation') && item.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button size="sm" className="bg-[#056A84] hover:bg-[#045A70] text-white text-xs" onClick={() => openResolve(item, 'action_taken')} data-testid={`report-action-${item.id}`}>
                            <CheckCircle className="w-3 h-3 mr-1" /> Atender
                          </Button>
                          <Button size="sm" variant="outline" className="text-xs" onClick={() => openResolve(item, 'dismissed')} data-testid={`report-dismiss-${item.id}`}>
                            <XCircle className="w-3 h-3 mr-1" /> Descartar
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
      )}

      <Dialog open={!!resolving} onOpenChange={() => setResolving(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{action === 'action_taken' ? 'Atender reporte' : 'Descartar reporte'}</DialogTitle>
            <DialogDescription>Puedes dejar una nota de resolución para el registro de auditoría</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Nota (opcional)</Label>
            <Input value={note} onChange={e => setNote(e.target.value)} placeholder="Ej: contenido retirado" data-testid="report-note-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolving(null)}>Cancelar</Button>
            <Button className="bg-[#056A84] hover:bg-[#045A70] text-white" onClick={handleResolve} disabled={saving} data-testid="report-resolve-button">
              {saving ? <Loader2 className="w-4 h-4 spinner mr-1" /> : null}Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
